import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { generateEventSlug } from '../utils/eventUtils';
import markdownComponents from '../utils/markdownComponents';
const EventListView = (props) => {
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [groupedEvents, setGroupedEvents] = useState({});
  const [isCurrentMonth, setIsCurrentMonth] = useState(false);
  const [showPastEvents, setShowPastEvents] = useState(false);

  // Update filtering logic to only handle past events
  useEffect(() => {
    setIsCurrentMonth(false);

    if (!props.events || props.events.length === 0) {
      setIsCurrentMonth(false);
      return;
    }
    
    let filtered = props.events; // Use events directly from props
        
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var curMonth = props.curMonth;
    var curYear = props.curYear;
    var isCurrentMonth = (today.getMonth() + 1 === curMonth) && (today.getFullYear() === curYear);
    setIsCurrentMonth(isCurrentMonth);
    
    if (isCurrentMonth && !showPastEvents) {
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.endDate || event.startDate);
        return eventDate >= today;
      });
    }
    
    setFilteredEvents(filtered);
    
    // Group events by date
    const grouped = {};
    filtered.forEach(event => {
      const date = new Date(event.startDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(event);
    });
    
    // Sort dates
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => new Date(a) - new Date(b))
      .forEach(date => {
        sortedGrouped[date] = grouped[date];
      });
    
    setGroupedEvents(sortedGrouped);
  }, [props.events, showPastEvents]);

  const toggleEventDetails = (eventId) => {
    setExpandedEventId(expandedEventId === eventId ? null : eventId);
  };

  // Toggle function for past events visibility
  const togglePastEvents = () => {
    setShowPastEvents(prevState => !prevState);
  };

  return (
    <div className="rounded-lg shadow-md opacity-[.93] bg-blue-100 p-4 w-full h-full overflow-y-auto" role="region" aria-label="Event List">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 mt-4 gap-4">
        {isCurrentMonth && (
          <button
            onClick={togglePastEvents}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded transition flex items-center self-start sm:self-center"
            aria-pressed={showPastEvents}
            aria-label={showPastEvents ? "Hide past events" : "Show past events"}
          >
            <span className="mr-2">{showPastEvents ? "Hide" : "Show"} Past Events</span>
            <i className={`fas fa-${showPastEvents ? "eye-slash" : "eye"}`} aria-hidden="true"></i>
          </button>
        )}
      </div>

      {props.events.length === 0 ? (
        <div className="bg-blue-50 rounded-lg p-8 text-center" role="status" aria-live="polite">
          <div className="flex flex-col items-center">
            <i className="fas fa-calendar-times text-blue-400 text-4xl mb-4" aria-hidden="true"></i>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Events Found</h3>
            <p className="text-gray-600">There are no events added for this month.</p>
          </div>
        </div>
      ) : Object.keys(groupedEvents).length === 0 ? (
        <div className="bg-blue-50 rounded-lg p-8 text-center" role="status" aria-live="polite">
          <div className="flex flex-col items-center">
            <i className="fas fa-filter text-blue-400 text-4xl mb-4" aria-hidden="true"></i>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Matching Events</h3>
            <p className="text-gray-600">No events match your selected filter.</p>
          </div>
        </div>
      ) : (
        <div role="list">
          {Object.entries(groupedEvents).map(([date, dateEvents]) => (
            <div key={date} className="mb-6 border-b pb-2" role="listitem">
              <h3 className="text-lg font-bold text-blue-600 mb-2">{date}</h3>
              
              <div className="space-y-3" role="list">
                {dateEvents.map(event => (
                  <div key={event.id} className={`bg-gray-50 rounded-lg shadow-sm p-3 items-center ${
                    event.eventType === 'Featured' ? 'bg-gradient-to-r from-blue-100 to-gray-100 border-4 border-yellow-400' : ''
                  }`} role="listitem">
                    <div className="flex justify-between items-start">
                      {event.sponsorLogo && (
                        <div className="mr-2 flex items-center">
                          <img src={event.sponsorLogo} alt={event.sponsorName || 'Event Sponsor'} className="max-h-8" />
                        </div>
                      )} 
                          {event.eventType === 'Featured' && (
                            <div className="bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded-full">Featured</div>
                          )}

                      <div className="flex-1">
                          <h4 className="font-bold text-gray-800">{event.title}</h4>                         
                        <p className="text-sm text-gray-600">{event.organization}</p>
                        {(new Date(event.startDate).getHours() !== 0 || new Date(event.startDate).getMinutes() !== 0) && (
                          <p className="text-sm font-semibold text-gray-700">
                            {new Date(event.startDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} -&nbsp;
                            {new Date(event.endDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </p>
                        )}
                      </div>                      
                      <button 
                        onClick={() => toggleEventDetails(event.id)}
                        className="bg-blue-100 hover:bg-blue-200 text-blue-700 font-bold py-1 px-3 rounded-full transition"
                        aria-expanded={expandedEventId === event.id}
                        aria-label={expandedEventId === event.id ? "Collapse details for " + event.title : "Expand details for " + event.title}
                      >
                        {expandedEventId === event.id ? '−' : '+'}
                      </button>
                      
                    </div>
                    {event.tags && event.tags.length > 0 && (
                        <div className="mb-4 text-center">
                            <div className="flex flex-wrap gap-2 mt-2 justify-center">
                                {event.tags.map((tag, index) => (
                                    <span 
                                        key={index} 
                                        className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded"
                                    >
                                        {tag.name}
                                    </span>
                                ))}
                                </div>
                            </div>
                        )}

                    {window.location.hash.includes('#email') && (
                        <div className="inline-block text-center py-3">
                            <a
                                href={`${window.location.origin}/#${generateEventSlug(event)}`}
                                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-2 text-center"
                            >                                
                                View Details
                            </a>
                        </div>
                    )}

                      
                    {expandedEventId === event.id && (
                      <div>
                      <div className="mt-3 pt-3 border-t border-gray-200" role="region" aria-label={`Details for ${event.title}`}>
                        <div className="mb-3">
                          <ReactMarkdown components={markdownComponents}>{event.description}</ReactMarkdown>
                        </div>
                        {event.eventURL && new Date(event.endDate || event.startDate) >= new Date(new Date().setHours(0, 0, 0, 0)) && (
                          <a 
                            href={event.eventURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded transition"
                            aria-label={`Go to event page for ${event.title}`}
                          >
                            {event.eventURL === '/private-events' ? 'About private events' : 'Go to event page'}
                          </a>
                        )}                        
                      </div>
                      <div className="inline-block text-center py-3">
                                                        <button
                                                    onClick={() => {
                                                        const slug = generateEventSlug(event);
                                                        const url = `${window.location.origin}/#${slug}`;
                                                        navigator.clipboard.writeText(url);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-2 text-center"
                                                >
                                                    <i className="fas fa-share-alt text-center"></i>
                                                    Share This (Copy to Clipboard)
                                                    </button>
                                                </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventListView; 